import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-vr.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import {
  commitRatingDoc,
  subscribeAllNonFinalRatingsByRatingID,
  subscribeFinalRatingDocForSlot,
} from '@ils/ratings/api/ratings.js';
import { ExamSlotFinalRatingDoc } from '@ils/ratings/db/finalRating.js';
import { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { Slot } from '@ils/slots/db/slot.js';
import { Specialization } from '@ils/static/enums.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import {
  catchError,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  firstValueFrom,
  fromEvent,
  map,
  type Observable,
  of,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import type { ILSUser } from '../../../db/ils-user.js';
import '../../components/video/ils-evaluation-video-chat.js';
import './ils-evaluation-rating.js';

@customElement('ils-evaluation-sync')
export class ILSEvaluationSync extends ChemistryLitElement {
  @property({ attribute: false }) slotID = new RXController<Maybe<string>>(this, undefined);
  @property({ attribute: false }) otherEvaluationNeeded = false;

  @state() private readonly _slot = new RXController(
    this,
    this.slotID.observable.pipe(
      filter(Boolean),
      switchMap(slotID => Slot.getByID<Slot>(slotID)(Slot.allFields)),
    ),
  );

  @state() private readonly _finalRating = new RXController(
    this,
    this.slotID.observable.pipe(
      filter(Boolean),
      switchMap(slotID => subscribeFinalRatingDocForSlot(slotID)(ExamSlotRatingDoc.allFields)),
      catchError(err => this._handleError()),
    ),
  );
  @state() private readonly _otherRatings = new RXController(
    this,
    this._finalRating.observable.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('_id'),
      switchMap(rating => {
        if (!(rating instanceof ExamSlotFinalRatingDoc)) return of([]);
        return subscribeAllNonFinalRatingsByRatingID(rating._id)(ExamSlotRatingDoc.allFields);
      }),
    ),
  );

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());
  @state() private readonly _ownRating = new RXController(
    this,
    combineLatest([
      this._currentUser.observable.pipe(filter(Boolean)),
      this._otherRatings.observable.pipe(filter(Boolean)),
    ]).pipe(
      map(([user, ratings]) => {
        const ownRating = ratings.find(r => r.inspector === user._id);
        return ownRating;
      }),
    ),
  );

  private _handleError(): Observable<null> {
    this.otherEvaluationNeeded = true;
    return of(null);
  }

  @state() private _selectedRatingView: 'rating' | 'tasks' = 'rating';

  private readonly _gridRef = createRef<HTMLDivElement>();
  private readonly _dragRef = createRef<HTMLDivElement>();
  @state() private _dragging = false;
  @state() private _currentSize = 0;
  private _dragStart = 0;
  private _dragX = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => {
        if (!this._dragging) return;
        this._dragX = e.clientX;
        this._updateLayout();
        e.preventDefault();
      });

    fromEvent<MouseEvent>(document, 'mouseup')
      .pipe(takeUntil(this.disconnected))
      .subscribe(_ => {
        if (!this._dragging) return;
        this._dragging = false;
      });
  }

  override render() {
    if (this.otherEvaluationNeeded) return this._error('Es haben noch nicht beide Bewerter eine Bewertung abgegeben.');
    if (this._slot.isLoading || this._finalRating.isLoading || this._ownRating.isLoading) return this._loader('');
    const slot = this._slot.value;
    const osce = slot?.osce;
    if (!slot || !osce) return this._error('Slot konnte nicht gefunden werden.');
    const finalRating = this._finalRating.value;
    if (!finalRating) return this._error('da ist etwas schiefgelaufen');

    return html`
      <x-flex padding="sm" h100>
        ${this._header(osce)}
        <div grid-container ${ref(this._gridRef)}>
          <x-flex
            padding="md"
            space="md"
            flex
            ${css({
              overflow: 'auto',
              maxHeight: 'calc(100vh - 150px)',
            })}
          >
            ${this._ratingSection(osce, finalRating)} ${this._submitButtons(finalRating)}
          </x-flex>
          <x-flex
            size-bar
            crossaxis-center
            ?dragging=${this._dragging}
            ${ref(this._dragRef)}
            @mousedown=${(e: MouseEvent) => {
              this._dragging = true;
              this._dragStart = e.clientX;
            }}
          >
            <x-vr></x-vr>
          </x-flex>
          <x-flex
            space="sm"
            ${css({
              overflow: 'auto',
              maxHeight: 'calc(100vh - 150px)',
            })}
          >
            <!-- Media (Audio & Text Notes), with the inspector's main note -->
            <ils-evaluation-video-chat room-id=${slot.roomID}> </ils-evaluation-video-chat>
            <ils-slot-detail rating-finished .examSlot=${slot} .rating=${this._ownRating.value}></ils-slot-detail>
          </x-flex>
        </div>
      </x-flex>
    `;
  }

  private _header(osce: OSCE) {
    return html`
      <x-flex id="header" horizontal crossaxis-center space="sm">
        <x-icon
          ${css({ fontSize: '28px', cursor: 'pointer' })}
          @click=${() => goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE })}
        >
          arrow_left_alt
        </x-icon>
        <x-h1> ${osce.titleTrainee} </x-h1>
      </x-flex>
    `;
  }

  private _ratingSection(osce: OSCE, finalRating: ExamSlotFinalRatingDoc) {
    return html`
      <x-tabs
        flex-none
        .selected=${this._selectedRatingView}
        @selected-changed=${(e: ValueEvent<'rating' | 'tasks'>) => (this._selectedRatingView = e.detail.value)}
      >
        <x-tab label="rating"> Bewertung </x-tab>
        <x-tab label="tasks"> Aufgabenstellung </x-tab>
      </x-tabs>
      <ils-evaluation-rating
        ${css({ display: this._selectedRatingView === 'rating' ? 'block' : 'none' })}
        .rating=${finalRating}
      ></ils-evaluation-rating>
      <ils-view-osce
        flex
        ${css({ display: this._selectedRatingView === 'rating' ? 'none' : 'block' })}
        .osce=${osce}
      ></ils-view-osce>
    `;
  }

  private _submitButtons(finalRating: ExamSlotFinalRatingDoc): Renderable {
    if (finalRating.committed)
      return html`
        <x-button
          ${css({ marginLeft: 'auto' })}
          @click=${() => goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE })}
        >
          Zurück zur Übersicht
        </x-button>
      `;
    const iHaveCommitted =
      this._currentUser.value?.specialization === Specialization.DOCTOR
        ? finalRating.doctorCommitted
        : finalRating.linguistCommitted;
    if (iHaveCommitted)
      return html`
        <x-button mode="outline" tone="error" ${css({ marginLeft: 'auto' })}> Gegenüber muss bestätigen </x-button>
      `;
    return html`
      <x-button
        ${css({ marginLeft: 'auto' })}
        ?disabled=${!finalRating.ratings.every(rating => rating.score !== -1)}
        @click=${async () => {
          this.shadowRoot?.getElementById('header')?.scrollIntoView({ behavior: 'smooth' });
          await XDialog.waitFor(Promise.all([commitRatingDoc(finalRating._id)(), firstValueFrom(timer(1000))]));
        }}
      >
        Bewertung abschließen
      </x-button>
    `;
  }

  private _loader(text: string) {
    return html`
      <x-flex space="lg" w100 h100 center crossaxis-center> <ils-loader> </ils-loader> <x-h3> ${text} </x-h3> </x-flex>
    `;
  }

  private _error(text: string) {
    return html`
      <x-flex space="lg" w100 h100 center crossaxis-center>
        <x-h3> ${text} </x-h3>
        <x-button tone="error" mode="outline" href="/start/evaluation-overview"> Zurück zur Übersicht </x-button>
      </x-flex>
    `;
  }

  private _ensureGridPixelValues() {
    const grid = this._gridRef.value;
    if (!grid) return;
    const [ratingSection, divider, mediaSection] = grid.children;
    if (!ratingSection || !divider || !mediaSection || this._currentSize !== 0) return;
    this._currentSize = ratingSection.clientWidth;
    grid.style.setProperty('grid-template-columns', `${this._currentSize}px max-content 1fr`);
  }

  private _updateLayout() {
    const grid = this._gridRef.value;
    const drag = this._dragRef.value;
    if (!grid || !drag) return;
    this._ensureGridPixelValues();
    const diff = this._dragX - this._dragStart;
    this._currentSize = this._currentSize + diff;
    this._dragStart = this._dragStart + diff;
    grid.style.setProperty('grid-template-columns', `${this._currentSize}px max-content 1fr`);
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
        '[size-bar]': {
          cursor: 'grab',
          padding: `0 ${this.spacing.sm}`,
          '&[dragging]': { cursor: 'grabbing' },
          '&:hover > x-vr': { background: this.colors.neutral.shade(50) },
        },
        '[grid-container]': {
          display: 'grid',
          gridTemplateColumns: '5fr max-content 2fr',
        },
      },
    ];
  }
}
